import { useState, useRef, useEffect } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import Turnstile, { TURNSTILE_ENABLED } from "./Turnstile";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";

const EMPTY = { name: "", email: "", company: "", deal_size: "", price: "", message: "" };

export default function DealInquiryModal({ open, onOpenChange, party = "", partyLabel = "" }) {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [token, setToken] = useState("");
  const [trackToken, setTrackToken] = useState("");
  const tsRef = useRef(null);

  useEffect(() => {
    if (open) { setSent(false); setF(EMPTY); setToken(""); setTrackToken(""); }
  }, [open]);

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const needsToken = TURNSTILE_ENABLED;

  const submit = async () => {
    setBusy(true);
    try {
      const res = await api.post("/public/deal-inquiry", { ...f, party, turnstile_token: token });
      setTrackToken(res.data?.track_token || "");
      setSent(true);
    } catch (e) {
      toast.error(apiError(e));
      setToken("");
      tsRef.current?.reset();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!bg-[#14171A] !border-[#24272B] !rounded-none !max-w-lg p-0 text-[#E8E6E1] max-h-[90vh] overflow-y-auto"
        data-testid="deal-inquiry-modal">
        <DialogTitle className="sr-only">Send us your deal</DialogTitle>
        <DialogDescription className="sr-only">Submit a private position for Solera to review. Company is required.</DialogDescription>
        <div className="p-8">
          {sent ? (
            <div data-testid="deal-inquiry-success">
              <span className="t-label">Received</span>
              <p className="t-body text-bone mt-3 measure">
                Thank you. We have the details of your position and a member of the team will respond directly.
                We have sent a confirmation to your email.
              </p>
              {trackToken && (
                <a href={`/deal-status/${trackToken}`} className="btn-ghost mt-6 mr-3 inline-block" data-testid="deal-track-link">Track your deal</a>
              )}
              <button className="btn-ghost mt-6" onClick={() => onOpenChange(false)} data-testid="deal-inquiry-close">Close</button>
            </div>
          ) : (
            <div>
              <span className="t-label">Send us your deal</span>
              {partyLabel && <p className="t-cap text-tint mt-2 fig">{partyLabel}</p>}
              <p className="t-small text-muted-s mt-3 measure">
                Tell us what you hold. We read the stock purchase agreement and confirm the transfer with the
                issuer before any price is discussed. Company is required, size and price are optional.
              </p>
              <div className="space-y-3 mt-6">
                <input className="input-solera" placeholder="Company (required)" value={f.company} onChange={set("company")} data-testid="deal-company-input" />
                <div className="grid grid-cols-2 gap-3">
                  <input className="input-solera" placeholder="Deal size (optional)" value={f.deal_size} onChange={set("deal_size")} data-testid="deal-size-input" />
                  <input className="input-solera" placeholder="Price (optional)" value={f.price} onChange={set("price")} data-testid="deal-price-input" />
                </div>
                <input className="input-solera" placeholder="Your name" value={f.name} onChange={set("name")} data-testid="deal-name-input" />
                <input className="input-solera" type="email" placeholder="Email" value={f.email} onChange={set("email")} data-testid="deal-email-input" />
                <textarea className="input-solera" rows={3} placeholder="Anything else we should know (optional)" value={f.message} onChange={set("message")} data-testid="deal-message-input" />
                <Turnstile ref={tsRef} onToken={setToken} />
                <button
                  className="btn-primary w-full"
                  disabled={busy || !f.company || !f.name || !f.email || (needsToken && !token)}
                  onClick={submit}
                  data-testid="deal-submit-button">
                  {busy ? "Sending" : "Send deal"}
                </button>
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
